import React, { type ReactNode } from 'react';
import { PiCheckBold } from 'react-icons/pi';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import Loading from 'src/modules/shared/components/atoms/loading';
import { Text } from 'src/modules/shared/components/atoms/text';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import type { ComponentVariant } from 'src/modules/shared/types/component.type';

export type ProgressStepState = 'active' | 'complete' | 'pending';

export interface ProgressStepsModalStep {
  /** Stable key used to render the step. */
  key: string;
  /** Primary step label. */
  label: ReactNode;
  /** Supporting text describing the current step behavior. */
  description: ReactNode;
  /** Current visual and semantic state of the step. */
  state: ProgressStepState;
}

export interface ProgressStepsModalProps {
  /** Whether the progress dialog is visible. */
  isOpen: boolean;
  /** Called when a dismissible dialog is closed. */
  onClose: () => void;
  /** Accessible and visible dialog title. */
  title: string;
  /** Supporting text displayed below the title. */
  subtitle?: string;
  /** Optional icon displayed beside the title. */
  icon?: ReactNode;
  /** Ordered progress steps. */
  steps: ProgressStepsModalStep[];
  /** Optional guidance displayed below the progress steps. */
  notice?: ReactNode;
  /** Semantic tone used by the active step. */
  activeVariant?: ComponentVariant;
  /** Semantic tone used by the optional guidance. */
  noticeVariant?: ComponentVariant;
  /** Whether Escape, the backdrop, and the header action may dismiss the modal. */
  dismissible?: boolean;
  /** Optional dialog sizing classes. */
  dialogClassName?: string;
}

const ACTIVE_INDICATOR_CLASSES: Record<ComponentVariant, string> = {
  primary: 'border-primary-200 bg-primary-50 text-primary-600',
  secondary: 'border-secondary-200 bg-secondary-50 text-secondary-600',
  neutral: 'border-neutral-200 bg-neutral-50 text-neutral-600',
  danger: 'border-danger-200 bg-danger-50 text-danger-600',
  warning: 'border-warning-200 bg-warning-50 text-warning-600',
  success: 'border-success-200 bg-success-50 text-success-600',
  info: 'border-info-200 bg-info-50 text-info-600',
  whatsapp: 'border-whatsapp-200 bg-whatsapp-50 text-whatsapp-600',
  call: 'border-call-200 bg-call-50 text-call-600',
  email: 'border-email-200 bg-email-50 text-email-600',
};

const NOTICE_BORDER_CLASSES: Record<ComponentVariant, string> = {
  primary: 'border-primary-200',
  secondary: 'border-secondary-200',
  neutral: 'border-neutral-200',
  danger: 'border-danger-200',
  warning: 'border-warning-200',
  success: 'border-success-200',
  info: 'border-info-200',
  whatsapp: 'border-whatsapp-200',
  call: 'border-call-200',
  email: 'border-email-200',
};

interface ProgressStepProps {
  step: ProgressStepsModalStep;
  activeVariant: ComponentVariant;
  hasConnector: boolean;
}

const ProgressStep: React.FC<ProgressStepProps> = ({
  step,
  activeVariant,
  hasConnector,
}) => (
  <div className="flex min-w-0 gap-3">
    <div className="flex w-8 shrink-0 flex-col items-center">
      <div
        className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${
          step.state === 'complete'
            ? 'border-success-200 bg-success-50 text-success-600'
            : step.state === 'active'
              ? ACTIVE_INDICATOR_CLASSES[activeVariant]
              : 'border-secondary-200 bg-secondary-50 text-secondary-400'
        }`}
        data-testid="progress-step-indicator"
        aria-hidden="true"
      >
        {step.state === 'complete' ? (
          <PiCheckBold className="h-4 w-4" />
        ) : step.state === 'active' ? (
          <Loading size="xs" variant={activeVariant} inline />
        ) : (
          <span className="h-2 w-2 rounded-full bg-secondary-300" />
        )}
      </div>
      {hasConnector ? (
        <div
          className="mt-1 min-h-5 w-px flex-1 bg-secondary-200"
          data-testid="progress-step-connector"
          aria-hidden="true"
        />
      ) : null}
    </div>
    <div className={hasConnector ? 'min-w-0 pb-5' : 'min-w-0 pb-1'}>
      <Text size="paragraph" weight="medium" className="text-secondary-900">
        {step.label}
      </Text>
      <Text size="small" color="secondary" className="mt-0.5">
        {step.description}
      </Text>
    </div>
  </div>
);

/** Displays an ordered, non-destructive multi-step operation in a modal. */
export const ProgressStepsModal: React.FC<ProgressStepsModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  steps,
  notice,
  activeVariant = 'primary',
  noticeVariant = 'warning',
  dismissible = true,
  dialogClassName = 'sm:max-w-lg',
}) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    dismissible={dismissible}
    ariaLabel={title}
    dialogClassName={dialogClassName}
    header={
      <div className="flex items-center gap-3 py-1">
        {icon ? (
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${ACTIVE_INDICATOR_CLASSES[activeVariant]}`}
          >
            {icon}
          </div>
        ) : null}
        <div className="min-w-0">
          <Text size="h6" weight="semibold" className="text-secondary-900">
            {title}
          </Text>
          {subtitle ? (
            <Text size="small" color="secondary" className="mt-0.5">
              {subtitle}
            </Text>
          ) : null}
        </div>
      </div>
    }
    body={
      <div className="w-full px-2 py-4 sm:px-3 sm:py-5" role="status">
        <div>
          {steps.map((step, index) => (
            <ProgressStep
              key={step.key}
              step={step}
              activeVariant={activeVariant}
              hasConnector={index < steps.length - 1}
            />
          ))}
        </div>

        {notice ? (
          <Alert
            className={`mt-5 border py-2.5 ${NOTICE_BORDER_CLASSES[noticeVariant]}`}
            variant={noticeVariant}
            appearance="soft"
            size="sm"
            message={
              typeof notice === 'string' ? (
                <Text size="small" color={noticeVariant} weight="medium">
                  {notice}
                </Text>
              ) : (
                notice
              )
            }
          />
        ) : null}
      </div>
    }
  />
);
